import styles from './SSOButton.module.scss';

export type SSOProvider = 'google' | 'microsoft' | 'github';

export interface SSOButtonProps {
  /** SSO provider */
  provider: SSOProvider;

  /** Button text (defaults to "Sign Up with {Provider}") */
  children?: React.ReactNode;

  /** Click handler */
  onClick?: () => void;

  /** Disabled state */
  disabled?: boolean;

  /** Additional CSS classes */
  className?: string;
}

const providerLabels: Record<SSOProvider, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  github: 'GitHub',
};

const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#google-clip)">
      <path
        d="M17.9905 9.16839C17.9905 8.43094 17.9293 7.89279 17.7967 7.33472H9.17928V10.6632H14.2375C14.1356 11.4904 13.5849 12.7361 12.3611 13.5732L12.3439 13.6846L15.0686 15.7473L15.2574 15.7657C16.991 14.201 17.9905 11.899 17.9905 9.16839Z"
        fill="#4285F4"
      />
      <path
        d="M9.17928 17.9382C11.6574 17.9382 13.7378 17.1409 15.2574 15.7657L12.3611 13.5732C11.586 14.1014 10.5458 14.4701 9.17928 14.4701C6.75213 14.4701 4.69212 12.9055 3.95778 10.743L3.85014 10.7519L1.01698 12.8945L0.979929 12.9952C2.48924 15.9251 5.58949 17.9382 9.17928 17.9382Z"
        fill="#34A853"
      />
      <path
        d="M3.9578 10.743C3.76404 10.1849 3.6519 9.58692 3.6519 8.96908C3.6519 8.35118 3.76404 7.75326 3.94761 7.19519L3.94247 7.07634L1.07381 4.8993L0.979953 4.94292C0.357892 6.15875 0.000951994 7.52407 0.000951994 8.96908C0.000951994 10.4141 0.357892 11.7794 0.979953 12.9952L3.9578 10.743Z"
        fill="#FBBC05"
      />
      <path
        d="M9.17928 3.46802C10.9027 3.46802 12.0653 4.19551 12.7282 4.80346L15.3185 2.33196C13.7277 0.886947 11.6574 0 9.17928 0C5.58949 0 2.48924 2.01305 0.979929 4.94292L3.94758 7.19519C4.69212 5.03265 6.75213 3.46802 9.17928 3.46802Z"
        fill="#EB4335"
      />
    </g>
    <defs>
      <clipPath id="google-clip">
        <rect width="18" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const MicrosoftIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#microsoft-clip)">
      <path d="M8.55449 8.55308H0V0H8.55449V8.55308Z" fill="#F1511B" />
      <path d="M18.0019 8.55308H9.44514V0H17.9996V8.55308H18.0019Z" fill="#80CC28" />
      <path d="M8.55449 18.0004H0V9.44729H8.55449V18.0004Z" fill="#00ADEF" />
      <path d="M18.0019 18.0004H9.44514V9.44729H17.9996V18.0004H18.0019Z" fill="#FBBC09" />
    </g>
    <defs>
      <clipPath id="microsoft-clip">
        <rect width="18" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const GitHubIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 0C4.02943 0 0 4.02943 0 9C0 12.9765 2.57812 16.3503 6.15469 17.5383C6.60469 17.6212 6.76875 17.3428 6.76875 17.1047C6.76875 16.8891 6.76094 16.1812 6.75703 15.4312C4.25391 15.9759 3.72656 14.3719 3.72656 14.3719C3.31641 13.3359 2.72656 13.0594 2.72656 13.0594C1.90898 12.4922 2.78906 12.5039 2.78906 12.5039C3.69141 12.5672 4.16719 13.4297 4.16719 13.4297C4.96875 14.8008 6.27344 14.4023 6.78516 14.1727C6.86719 13.5914 7.10156 13.1953 7.35938 12.9703C5.36016 12.7453 3.25781 11.9766 3.25781 8.52656C3.25781 7.54453 3.61328 6.74297 4.18594 6.11719C4.09219 5.89063 3.78281 4.97578 4.27422 3.73828C4.27422 3.73828 5.02969 3.49844 6.74531 4.64766C7.46484 4.44844 8.23594 4.34844 9.00234 4.34453C9.76641 4.34844 10.5375 4.44844 11.2594 4.64766C12.9727 3.49844 13.7266 3.73828 13.7266 3.73828C14.2195 4.97578 13.9102 5.89063 13.8188 6.11719C14.393 6.74297 14.7445 7.54453 14.7445 8.52656C14.7445 11.9859 12.6375 12.7422 10.6313 12.9648C10.9547 13.2445 11.2406 13.7953 11.2406 14.6344C11.2406 15.8367 11.2297 16.8047 11.2297 17.1047C11.2297 17.3453 11.3914 17.6258 11.8477 17.5367C15.4195 16.3477 17.9953 12.9742 17.9953 9C17.9953 4.02943 13.9659 0 9 0Z"
      fill="#181616"
    />
  </svg>
);

const providerIcons: Record<SSOProvider, React.FC> = {
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
  github: GitHubIcon,
};

export const SSOButton = ({
  provider,
  children,
  onClick,
  disabled = false,
  className = '',
}: SSOButtonProps) => {
  const Icon = providerIcons[provider];
  const defaultText = `Sign Up with ${providerLabels[provider]}`;

  const buttonClassName = [styles.ssoButton, disabled ? styles.disabled : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={buttonClassName}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      <span className={styles.icon}>
        <Icon />
      </span>
      <span className={styles.text}>{children || defaultText}</span>
      <span className={styles.spacer} />
    </button>
  );
};
